import { useEffect, useState } from "react";
import { X, ArrowRight, UserPlus, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import PragmaSelect from "./PragmaSelect";

const ROLE_OPTIONS = [
  { value: "sales_rep", label: "Sales Rep" },
  { value: "manager",   label: "Manager"   },
  { value: "admin",     label: "Admin"     },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
}

export default function InviteTeammateModal({ open, onClose, currentSeats, onInvited }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", role: "sales_rep", password: "",
  });
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset form + fetch the prorated cost preview whenever the modal opens
  useEffect(() => {
    if (!open) return;
    setForm({ first_name: "", last_name: "", email: "", role: "sales_rep", password: "" });
    setErr("");
    setSubmitting(false);
    setPreview(null);
    setPreviewLoading(true);
    api.get(`/billing/seat-change-preview?new_quantity=${currentSeats + 1}`)
      .then(({ data }) => setPreview(data))
      .catch(() => setPreview(null))
      .finally(() => setPreviewLoading(false));
  }, [open, currentSeats]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/users", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        role: form.role,
        password: form.password,
      });
      const newMonthly = data?.billing?.new_monthly_usd;
      toast.success(
        newMonthly != null
          ? `${data.name || data.email} invited · subscription now $${newMonthly.toFixed(2)} / month`
          : `${data.name || data.email} invited 🎉`
      );
      onInvited?.(data);
      onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not invite teammate");
      setSubmitting(false);
    }
  };

  const hasSub = preview?.has_subscription;
  const proratedNow = preview?.prorated_now_usd ?? 0;
  const newMonthly = preview?.new_monthly_usd ?? 0;

  return (
    <div className="invite-modal-backdrop" onMouseDown={onClose} data-testid="invite-modal">
      <div className="invite-modal" onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="invite-modal-close" onClick={onClose} aria-label="Close" data-testid="invite-modal-close">
          <X strokeWidth={2.2} />
        </button>

        <div className="invite-modal-header">
          <div className="invite-modal-icon"><UserPlus strokeWidth={2.2} /></div>
          <div>
            <h2 className="invite-modal-title">Invite a teammate</h2>
            <p className="invite-modal-sub">
              They'll get instant access · Team grows from {currentSeats} to {currentSeats + 1} seat{currentSeats + 1 === 1 ? "" : "s"}.
            </p>
          </div>
        </div>

        {/* ─── Charge preview ─── */}
        <div className="invite-modal-cost" data-testid="invite-modal-cost">
          {previewLoading ? (
            <div className="invite-modal-cost-loading">Calculating proration…</div>
          ) : hasSub ? (
            <>
              <div className="invite-modal-cost-row">
                <span>Charged today (prorated)</span>
                <strong data-testid="invite-modal-prorated">${proratedNow.toFixed(2)}</strong>
              </div>
              <div className="invite-modal-cost-row">
                <span>Renewal from {fmtDate(preview?.current_period_end)}</span>
                <strong data-testid="invite-modal-new-monthly">${newMonthly.toFixed(2)} / mo</strong>
              </div>
            </>
          ) : (
            <div className="invite-modal-cost-row">
              <span>You're on the free trial — no charge today.</span>
              <strong>${newMonthly.toFixed(2)} / mo after trial</strong>
            </div>
          )}
        </div>

        <form onSubmit={submit} data-testid="invite-modal-form">
          <div className="invite-modal-row-2">
            <div>
              <label className="invite-modal-label">First name</label>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="invite-modal-input"
                placeholder="Jane"
                data-testid="invite-first-name"
                required
              />
            </div>
            <div>
              <label className="invite-modal-label">Last name</label>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="invite-modal-input"
                placeholder="Doe"
                data-testid="invite-last-name"
              />
            </div>
          </div>

          <label className="invite-modal-label">Work email</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="invite-modal-input"
            placeholder="jane@company.com"
            data-testid="invite-email"
            required
          />

          <div className="invite-modal-row-2">
            <div>
              <label className="invite-modal-label">Role</label>
              <PragmaSelect
                value={form.role}
                onChange={(v) => setForm({ ...form, role: v })}
                options={ROLE_OPTIONS}
                testId="invite-role"
              />
            </div>
            <div>
              <label className="invite-modal-label">Temporary password</label>
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="invite-modal-input"
                placeholder="Min. 8 chars"
                minLength={8}
                data-testid="invite-password"
                required
              />
            </div>
          </div>

          {err && (
            <div className="invite-modal-error" data-testid="invite-modal-error">
              <AlertCircle strokeWidth={2.2} /> {err}
            </div>
          )}

          <div className="invite-modal-actions">
            <button
              type="button"
              className="invite-modal-cancel"
              onClick={onClose}
              data-testid="invite-modal-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="invite-modal-submit"
              disabled={submitting || previewLoading}
              data-testid="invite-modal-submit"
            >
              {submitting
                ? "Inviting…"
                : hasSub && proratedNow > 0
                  ? <>Invite & charge ${proratedNow.toFixed(2)} <ArrowRight strokeWidth={2.2} /></>
                  : <>Send invite <ArrowRight strokeWidth={2.2} /></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
